import React, {
  ReactNode,
  useEffect,
  useState,
} from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtom, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  accountHomeBaseUrl,
  uiRoutes,
  useAccountInfo,
  useBillingPeriods,
  useFullPayoffAmount,
  useLoanSummary,
  useMakeAPaymentBlocker,
  useMakePayment,
  usePaymentCalendarDates,
  usePaymentMethods,
  useScheduledPayments,
  useTracking,
} from '@consumer-frontend/nc-api';
import {
  ACH_NEXT_DAY_CUTOFF,
  currencyString,
  getReactNodeText,
  isDefined,
  isLineOfCredit,
  LOAN_STATUS,
  NcMakeAPaymentFormFieldsT,
  noOpenLoanErrorHeader,
  noOpenLoanErrorMessage,
  nowCentral,
  PaymentDetailsT,
  productTypeDisplayFromType,
  redirectTo,
  today,
  tomorrow,
} from '@consumer-frontend/nc-utils';
import { Container } from '@consumer-frontend/shared-ui';

import {
  accountIdAtom,
  hasFetchErrorAtom,
  MAKE_PAYMENT_ERRORS_MAP,
  paymentDetailsAtom,
  paymentFailureDataAtom,
} from '../../store';
import { NcError } from '../NcError';
import { NcErrorPage } from '../NcErrorPage';
import { NcGoHomeButton } from '../NcGoHomeButton';
import { NcLinkStyledAsButton } from '../NcLinkStyledAsButton';
import { NcMakeAPaymentForm } from '../NcMakeAPaymentForm';
import { NcReviewPaymentDetail } from '../NcReviewPaymentDetail';
import { NcScheduledPayments } from '../NcScheduledPayments';

// eslint-disable-next-line import/order
import '@consumer-bootstrap/netcredit.scss';
import '@consumer-bootstrap/brands/netcredit/payment/map-page.scss';
import '@consumer-bootstrap/brands/netcredit/payment/review.scss';

export type NcMakeAPaymentT = React.FC;

export const NcMakeAPayment: NcMakeAPaymentT = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Local state
  const [isEditing, setIsEditing] = useState(true);
  const [alertMessage, setAlertMessage] = useState<ReactNode>('');

  // Shared state
  const [accountId] = useAtom(accountIdAtom);
  const [hasFetchError, setHasFetchError] = useAtom(hasFetchErrorAtom);
  const setPaymentFailureData = useSetAtom(paymentFailureDataAtom);
  const [paymentDetails, setPaymentDetails] = useAtom(paymentDetailsAtom);

  // Fetch dependent data
  const { accountInfo, isLoading: isAccountInfoLoading } = useAccountInfo(accountId);
  const { loanNumber, loanType } = accountInfo || {};
  const {
    isLoading: isLoanSummaryLoading,
    loanSummary: {
      currentOwner,
      pastDueBalance,
      loanStatus,
    } = {},
  } = useLoanSummary(accountId, loanNumber);

  const isPastDue = loanStatus === LOAN_STATUS.PAST_DUE;

  // Watch loading states of dependent API calls
  const {
    currentBankAccount,
    currentDebitCard,
    isLoading: areMethodsLoading,
  } = usePaymentMethods(accountId);
  const {
    isLoading: areDatesLoading,
  } = usePaymentCalendarDates(
    accountId,
    loanNumber,
    'debit_card',
  );
  // Conditionally adding loanNumber for LOCs only to not make request for
  // Installment loans as they dont have Billing Periods
  const { isLoading: areBillingPeriodsLoading } = useBillingPeriods({
    accountId,
    loanNumber: isLineOfCredit(loanType) ? loanNumber : undefined,
  });
  const { isLoading: areScheduledPaymentsLoading } = useScheduledPayments({ accountId, loanNumber });

  const {
    error: makePaymentAxiosError,
    isMutating: isPaymentMutating,
    makePaymentResp: { successType } = {},
    reset: resetMakePaymentState,
    status,
    trigger: triggerPayment,
  } = useMakePayment({
    accountId,
    loanNumber,
    paymentDetails,
  });

  // This is only used to ensure that there is a non-zero balance when this page loads.
  // The call used for the radio button value occurs in the MaPForm component.
  const { amount: balance, isLoading: isFullPayoffAmountLoading } = useFullPayoffAmount({
    accountId,
    loanNumber,
    paymentDate: today(),
  });

  const isLoading = isAccountInfoLoading
    || isLoanSummaryLoading
    || areMethodsLoading
    || areDatesLoading
    || isFullPayoffAmountLoading
    || areBillingPeriodsLoading
    || areScheduledPaymentsLoading
  ;

  const hasValidPaymentMethod = (
    (!!currentDebitCard && currentDebitCard.isValid)
    || (!!currentBankAccount && currentBankAccount.isValid)
  );

  const {
    errorType: blockerErrorType,
    header: blockerHeader,
    message: blockerMessage,
    shouldBlock,
  } = useMakeAPaymentBlocker({
    balance,
    currentOwner,
    hasValidPaymentMethod,
    isLoading,
    loanStatus,
  });

  const {
    trackEvent,
    trackPage,
    isMutating: isTrackingMutating,
  } = useTracking(accountId);

  // EFFECTS
  // Always scroll to top on mount
  useEffect(
    () => {
      window.scrollTo(0, 0);
    },
    [], // Runs only on page load
  );

  // Show past-due alert if past due amount exists
  useEffect(
    () => {
      if (pastDueBalance && isPastDue) {
        setAlertMessage(<span>Your account is past due by <strong>{currencyString(pastDueBalance)}</strong>.</span>);
      }
    },
    [pastDueBalance, isPastDue],
  );

  // Show default error message if any fetch fails
  useEffect(
    () => {
      if (hasFetchError) {
        setHasFetchError(() => false);
        navigate(uiRoutes.dashboard.paymentError);
      }
    },
    [
      hasFetchError,
      navigate,
      setHasFetchError,
    ],
  );

  // Re-open form and show error or navigate to error page if POST /payments fails
  useEffect(
    () => {
      if (isDefined(makePaymentAxiosError)) {
        datadogRum.addAction(
          'makePaymentError',
          {
            code: makePaymentAxiosError.code,
            message: makePaymentAxiosError.message,
            name: makePaymentAxiosError.name,
            responseData: makePaymentAxiosError.response?.data,
            status,
          },
        );
      }

      if (isDefined(makePaymentAxiosError) && (!isDefined(status) || status === 0)) {
        // Use navigate once account summary has been redesigned
        redirectTo(uiRoutes.legacyAH.accountSummary);

        return;
      }

      const makePaymentError = makePaymentAxiosError?.response?.data;

      if (!isDefined(makePaymentError)) { return; }

      const eventData = {
        error: makePaymentError,
        loan_number: loanNumber,
        loan_status: loanStatus,
        num_days_until_target_date: isDefined(paymentDetails?.paymentDate)
          ? paymentDetails.paymentDate.compare(today())
          : undefined,
        payment_amount: paymentDetails?.paymentAmount,
        payment_amount_type: 'TODO', // This will be addressed in a future story.
        payment_method: paymentDetails?.paymentMethod,
        payment_target_date: paymentDetails?.paymentDate?.toString(),
      };

      // Change-page errors:
      // Treat unsupported API validation errors as 500s and redirect both to payment error page.
      if ((isDefined(status) && status >= 500)
        || !isDefined(makePaymentError.error_type)
        || !isDefined(MAKE_PAYMENT_ERRORS_MAP[makePaymentError.error_type])
      ) {
        setPaymentFailureData({
          event_data: eventData,
          event_description: 'paymentErrorLoaded',
        });

        navigate(uiRoutes.dashboard.paymentError);

        return;
      }

      // Same-page errors:
      // If error is not 500 and is supported, show error above form.
      const errorMessage = makePaymentError.error_type === 'invalid_cfpb_date'
        ? makePaymentError.message
        : MAKE_PAYMENT_ERRORS_MAP[makePaymentError.error_type]
      ;

      setAlertMessage(errorMessage);
      setIsEditing(true);
      window.scrollTo(0, 0);

      // Firing these tracking calls in an async IIFE to ensure the trackPage completes before
      // the trackEvent fires in order to associate the event with the correct page_view_id.
      (async () => {
        // Don't let tracking failures bork the page (or tests as it often does when API errors are not caught)
        try {
          await trackPage({
            tracked_action: 'paymentFailure',
            tracked_controller: 'reactComponent',
            tracked_url: `${accountHomeBaseUrl}${location.pathname}`,
          });
          await trackEvent({
            event_data: {
              ...eventData,
              error: {
                ...makePaymentError,
                message: getReactNodeText(errorMessage),
              },
            },
            event_description: 'paymentFailureLoaded',
          });
        } catch {
          /* Swallow tracking errors */
        }
      })();
    },
    [
      loanNumber,
      loanStatus,
      location.pathname,
      makePaymentAxiosError,
      navigate,
      paymentDetails,
      setPaymentFailureData,
      status,
      trackEvent,
      trackPage,
    ],
  );

  // Update paymentDetails.successType on successful POST /payments
  useEffect(
    () => {
      if (isDefined(successType)) {
        setPaymentDetails((prev) => ({ ...prev, successType }) as PaymentDetailsT);
        navigate(uiRoutes.dashboard.paymentConfirmation);
      }
    },
    [
      navigate,
      setPaymentDetails,
      successType,
    ],
  );

  // Track page view transitions between form and review in DataDog
  useEffect(
    () => {
      const viewName = isEditing ? 'Form' : 'Review';

      datadogRum.addAction(`NcMakeAPayment${viewName}View`);
    },
    [isEditing],
  );

  // Track event for blocked users.
  useEffect(
    () => {
      if (!shouldBlock) { return; }
      const eventData = {
        error_message: `${getReactNodeText(blockerHeader)}\n${getReactNodeText(blockerMessage)}`,
        error_type: blockerErrorType,
        loan_number: loanNumber,
        loan_status: loanStatus,
      };

      try {
        trackEvent({
          event_data: eventData,
          event_description: 'paymentFormNotShown',
        });
        datadogRum.addAction('paymentFormNotShown', eventData);
      } catch {
        /* Swallow tracking errors */
      }
    },
    [
      blockerErrorType,
      blockerHeader,
      blockerMessage,
      loanNumber,
      loanStatus,
      shouldBlock,
      trackEvent,
    ],
  );

  // Event handlers and helpers
  const reviewPageEventData = (data: NcMakeAPaymentFormFieldsT) => ({
    full_payoff_amount: data.fullPayoffAmount,
    loan_number: loanNumber,
    loan_status: loanStatus,
    num_days_until_target_date: isDefined(data?.paymentDate) && data.paymentDate.compare(today()),
    payment_amount: data?.paymentAmount,
    payment_amount_type: 'TODO', // This will be addressed in a future story.
    payment_messaging: getReactNodeText(data?.paymentMessaging),
    payment_method: data?.paymentMethod,
    payment_target_date: data?.paymentDate?.toString(),
    product: loanType,
  });

  // This does not fire if form validation fails.
  const onSubmit = async (formData: NcMakeAPaymentFormFieldsT) => {
    resetMakePaymentState();

    setPaymentDetails({
      ...formData,
      accountLast4: formData.paymentMethod === 'ach'
        ? currentBankAccount?.last4AccountNumber || ''
        : currentDebitCard?.last4 || '',
      paymentMessaging: getReactNodeText(formData.paymentMessaging),
      paymentOwner: currentOwner,
      paymentSource: formData.paymentMethod === 'ach'
        ? currentBankAccount?.bankName || ''
        : currentDebitCard?.cardBrand || '',
      productTypeDisplay: productTypeDisplayFromType(loanType),
      routingLast4: formData.paymentMethod === 'ach'
        ? currentBankAccount?.last4RoutingNumber
        : undefined,
    } as PaymentDetailsT);

    try {
      await trackPage({
        tracked_action: 'paymentReview',
        tracked_controller: 'reactComponent',
        tracked_url: `${accountHomeBaseUrl}${location.pathname}`,
      });
      await trackEvent({
        event_data: reviewPageEventData(formData),
        event_description: 'paymentReviewLoaded',
      });
    } catch {
      /* Swallow tracking errors */
    }

    setIsEditing(false);
    setAlertMessage('');
    window.scrollTo(0, 0);
  };

  const onAuthorizePayment = async () => {
    if (isTrackingMutating || isPaymentMutating) { return; }

    // ACH cutoff check
    if (paymentDetails?.paymentMethod === 'ach'
      && nowCentral().hour >= ACH_NEXT_DAY_CUTOFF
      && paymentDetails.paymentDate === tomorrow()
    ) {
      setAlertMessage(MAKE_PAYMENT_ERRORS_MAP.invalid_date);
      setIsEditing(true);
      window.scrollTo(0, 0);

      // Using setTimeout to ensure the "page transition" completes before the trackEvent fires.
      setTimeout(
        () => {
          trackEvent({
            event_data: {
              current_time: today().toString(),
              error_type: 'ach_cutoff',
              loan_number: loanNumber,
              loan_status: loanStatus,
              payment_amount: paymentDetails.paymentAmount,
              payment_amount_type: 'TODO', // This will be addressed in a future story.
              payment_method: 'ach',
              payment_target_date: paymentDetails.paymentDate?.toString(),
            },
            event_description: 'paymentFailureLoaded',
          }).catch(() => {
            /* Swallow tracking errors */
          });
        },
        1000,
      );

      return;
    }

    try {
      await trackEvent({
        event_data: reviewPageEventData(paymentDetails as NcMakeAPaymentFormFieldsT),
        event_description: 'paymentAuthorized',
      });
    } catch (err) {
      // Swallow errors here so they don't get logged as an error in console twice.
    }

    try {
      await triggerPayment();
    } catch (err) {
      // Swallow errors here so they don't get logged as an error in console twice.
      // The makePaymentError is handled in useEffect above.
    }
  };

  const onEditPayment = async () => {
    try {
      await trackEvent({
        event_data: { loan_number: loanNumber },
        event_description: 'paymentEditClicked',
      });
      await trackPage({
        tracked_action: 'paymentEdit',
        tracked_controller: 'reactComponent',
        tracked_url: `${accountHomeBaseUrl}${location.pathname}`,
      });
    } catch {
      // Swallow errors here so they don't get logged as an error in console twice.
    }

    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  // Render no-open-loan error screen
  if (accountInfo && !loanNumber) {
    return (
      <Container className="nc-make-a-payment" hasBoxShadow>
        <h1 className="h2">Make a Payment</h1>

        <NcError header={noOpenLoanErrorHeader} message={noOpenLoanErrorMessage} />

        <NcGoHomeButton />
      </Container>
    );
  }

  return (
    <div className="nc-make-a-payment">

      {!isLoading && shouldBlock && (
        <NcErrorPage
          heading="Make a Payment"
          message={
            <>
              <p className="fw-bold">{blockerHeader}</p>
              <p>{blockerMessage}</p>
            </>
          }
          fullWidth
        >
          <NcLinkStyledAsButton
            to={accountHomeBaseUrl}
            buttonText="Return to Account Home"
            fullWidth
          />
        </NcErrorPage>
      )}

      {isEditing && !shouldBlock && (
        <>
          <NcMakeAPaymentForm
            {...paymentDetails}
            alertMessage={alertMessage}
            isLoading={isLoading}
            onSubmit={onSubmit}
          />

          <NcScheduledPayments isLoading={isLoading} />
        </>
      )}

      {!isEditing && !shouldBlock && paymentDetails && (
        <NcReviewPaymentDetail
          isLoading={isPaymentMutating || isTrackingMutating}
          onAuthorizePayment={onAuthorizePayment}
          onEditPayment={onEditPayment}
          paymentDetails={paymentDetails}
        />
      )}
    </div>
  );
};
